import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Swap, Plus, Trash, PencilSimple, Calculator, WarningCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const NUTRIENTS = [
  { value: 'hidratos_g', label: 'Hidratos de carbono (g)' },
  { value: 'proteina_g', label: 'Proteína (g)' },
  { value: 'gordura_g', label: 'Gordura (g)' },
  { value: 'energia_kcal', label: 'Energia (kcal)' },
];

function FoodSearch({ onSelect, placeholder, autoFocus }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!search || search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await axios.get(`${API}/api/foods?search=${encodeURIComponent(search)}&limit=8`); setResults(data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [search]);
  return (
    <div className="relative">
      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder || 'Procurar alimento...'} className="rounded-xl border-brand-border text-sm" autoFocus={autoFocus} data-testid="food-search-equiv" />
      {results.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-brand-border rounded-xl shadow-lg max-h-40 overflow-y-auto">
          {results.map(f => (
            <button key={f.id} type="button" onClick={() => { onSelect(f); setSearch(''); setResults([]); }} className="w-full text-left px-3 py-2 text-xs hover:bg-brand-mint/10 border-b border-brand-border/30 last:border-0">
              <span className="font-medium text-brand-text">{f.name}</span>
              <div className="text-[9px] text-brand-muted mt-0.5">{f.energia_kcal}kcal | P:{f.proteina_g}g | HC:{f.hidratos_g}g | G:{f.gordura_g}g</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EquivalentesPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [groups, setGroups] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [nutrient, setNutrient] = useState('hidratos_g');
  const [refFood, setRefFood] = useState(null); // {id, name, ...nutrition}
  const [refQty, setRefQty] = useState('100');
  const [altFoods, setAltFoods] = useState([]); // [{food, calculated_qty, error}]
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchGroups = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/equivalent-groups`); setGroups(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const resetForm = () => {
    setName(''); setNutrient('hidratos_g'); setRefFood(null); setRefQty('100');
    setAltFoods([]); setCalcResult(null); setEditId(null);
  };

  const openEdit = (g) => {
    setEditId(g.id);
    setName(g.name);
    setNutrient(g.reference_nutrient || 'hidratos_g');
    const rf = g.reference_food || {};
    if (rf.food_id) {
      setRefFood({ id: rf.food_id, name: rf.food_name, ...rf });
      setRefQty(String(rf.quantity || 100));
    }
    // Rebuild altFoods from saved foods
    setAltFoods((g.foods || []).map(f => ({ food: { id: f.food_id, name: f.food_name }, calculated_qty: f.equivalent_portion, measure_display: f.measure_display || null, measure_name: f.measure_name || null, measure_qty: f.measure_qty || null })));
    setShowForm(true);
  };

  const addAltFood = (food) => {
    if (altFoods.some(a => a.food.id === food.id)) { toast.error('Alimento já adicionado'); return; }
    if (refFood && food.id === refFood.id) { toast.error('Não podes usar o mesmo alimento como referência e alternativa'); return; }
    setAltFoods([...altFoods, { food, calculated_qty: null, error: null }]);
    setCalcResult(null);
  };

  const removeAltFood = (idx) => {
    setAltFoods(altFoods.filter((_, i) => i !== idx));
    setCalcResult(null);
  };

  const handleCalculate = async () => {
    if (!refFood || altFoods.length === 0) { toast.error('Seleciona alimento de referência e pelo menos uma alternativa'); return; }
    setCalculating(true);
    try {
      const { data } = await axios.post(`${API}/api/equivalent-groups/calculate`, {
        reference_food_id: refFood.id,
        reference_quantity: parseFloat(refQty) || 100,
        nutrient,
        alternative_food_ids: altFoods.map(a => a.food.id),
      });
      setCalcResult(data);
      if (data.error) {
        toast.error(data.message);
      } else {
        const updated = altFoods.map(a => {
          const r = data.results?.find(res => res.food_id === a.food.id);
          return r ? { ...a, calculated_qty: r.equivalent_portion, error: r.error || null, measure_display: r.measure_display || null, measure_name: r.measure_name || null, measure_qty: r.measure_qty || null } : a;
        });
        setAltFoods(updated);
        toast.success('Equivalências calculadas');
      }
    } catch { toast.error('Erro no cálculo'); }
    finally { setCalculating(false); }
  };

  const save = async () => {
    if (!refFood) { toast.error('Seleciona um alimento de referência'); return; }
    const foods = altFoods.filter(a => a.food?.id).map(a => ({
      food_id: a.food.id, food_name: a.food.name,
      equivalent_portion: a.calculated_qty || '', unit: 'g',
      measure_display: a.measure_display || null,
      measure_name: a.measure_name || null,
      measure_qty: a.measure_qty || null,
    }));
    const payload = {
      name, reference_nutrient: nutrient,
      reference_food: { food_id: refFood.id, food_name: refFood.name, quantity: parseFloat(refQty) || 100 },
      foods,
    };
    try {
      if (editId) { await axios.put(`${API}/api/equivalent-groups/${editId}`, payload); }
      else { await axios.post(`${API}/api/equivalent-groups`, payload); }
      toast.success('Grupo guardado'); setShowForm(false); resetForm(); fetchGroups();
    } catch { toast.error('Erro ao guardar'); }
  };

  const del = async (id) => {
    const g = groups.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o grupo de equivalentes "${g?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/equivalent-groups/${id}`); toast.success('Grupo eliminado'); fetchGroups();
  };
  const nutrientLabel = (key) => NUTRIENTS.find(n => n.value === key)?.label || key;

  return (
    <div className="fade-in" data-testid="equivalentes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text" data-testid="equivalentes-title">Grupos de Equivalentes</h2>
          <p className="text-sm text-brand-muted mt-1">Trocas alimentares com cálculo automático</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-group-btn">
          <Plus size={16} /> Novo grupo
        </button>
      </div>

      {loading ? <div className="text-center py-12 text-brand-muted">A carregar...</div> : groups.length === 0 ? (
        <div className="text-center py-16">
          <Swap size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">Sem grupos de equivalentes.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(g => (
            <div key={g.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`equiv-group-${g.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-heading font-medium text-brand-text">{g.name}</h3>
                  {g.reference_nutrient && <p className="text-[10px] text-brand-muted">Referência: {nutrientLabel(g.reference_nutrient)}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(g)} className="p-1.5 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                  <button onClick={() => del(g.id)} className="p-1.5 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
                </div>
              </div>
              {/* Reference food */}
              {g.reference_food?.food_name && (
                <div className="bg-brand-green/5 border border-brand-green/15 rounded-xl px-3 py-2 mb-2">
                  <p className="text-xs text-brand-green font-medium">{g.reference_food.quantity || 100}g {g.reference_food.food_name}</p>
                </div>
              )}
              {/* Alternatives */}
              {g.foods?.length > 0 && (
                <div className="space-y-1">
                  {g.foods.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-[10px] text-brand-green font-semibold w-5">ou</span>
                      <span className="text-xs bg-brand-mint/15 text-brand-green px-2.5 py-1 rounded-full">
                        {f.measure_display ? (
                          <><b>{f.measure_display}</b> de {f.food_name} ({f.equivalent_portion}g)</>
                        ) : (
                          <><b>{f.equivalent_portion}g</b> {f.food_name}</>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar grupo' : 'Novo grupo de equivalentes'}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Group name */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Nome do grupo</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Cereais e derivados" className="rounded-xl border-brand-border" required data-testid="group-name-input" />
            </div>

            {/* Reference nutrient */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Nutriente de referência</Label>
              <p className="text-[10px] text-brand-placeholder mb-2">A equivalência será calculada com base neste nutriente</p>
              <div className="flex flex-wrap gap-1.5">
                {NUTRIENTS.map(n => (
                  <button key={n.value} type="button" onClick={() => { setNutrient(n.value); setCalcResult(null); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${nutrient === n.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`nutrient-${n.value}`}>
                    {n.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reference food */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Alimento de referência</Label>
              {refFood ? (
                <div className="bg-brand-green/5 border border-brand-green/15 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-brand-green">{refFood.name}</span>
                    <button type="button" onClick={() => { setRefFood(null); setCalcResult(null); }} className="text-[10px] text-brand-muted hover:text-red-500">Alterar</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={refQty} onChange={e => { setRefQty(e.target.value); setCalcResult(null); }} className="w-24 rounded-xl border-brand-border text-sm" data-testid="ref-quantity" />
                    <span className="text-xs text-brand-muted">gramas</span>
                  </div>
                </div>
              ) : (
                <FoodSearch onSelect={f => { setRefFood(f); setCalcResult(null); }} placeholder="Procurar alimento de referência..." autoFocus />
              )}
            </div>

            {/* Alternative foods */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Alimentos alternativos</Label>
              <p className="text-[10px] text-brand-placeholder mb-2">Adiciona os alimentos que podem substituir o de referência</p>
              {altFoods.length > 0 && (
                <div className="space-y-2 mb-3">
                  {altFoods.map((a, i) => (
                    <div key={i} className="bg-brand-bg/50 rounded-xl p-3 border border-brand-border/40" data-testid={`alt-food-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-brand-text font-medium">{a.food.name}</span>
                        <button type="button" onClick={() => removeAltFood(i)} className="text-red-400 hover:text-red-600"><Trash size={13} /></button>
                      </div>
                      {a.calculated_qty !== null && !a.error && (
                        <div className="mt-1">
                          {a.measure_display ? (
                            <p className="text-xs text-brand-green font-semibold">{a.measure_display} de {a.food.name} ({a.calculated_qty}g)</p>
                          ) : (
                            <p className="text-xs text-brand-green font-semibold">{a.calculated_qty}g equivalente</p>
                          )}
                        </div>
                      )}
                      {a.error && (
                        <p className="text-xs text-red-500 flex items-center gap-1 mt-1"><WarningCircle size={12} /> {a.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <FoodSearch onSelect={addAltFood} placeholder="Adicionar alimento alternativo..." />
            </div>

            {/* Calculate button */}
            {refFood && altFoods.length > 0 && (
              <button type="button" onClick={handleCalculate} disabled={calculating} className="w-full bg-brand-gold text-white rounded-full py-3 font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="calculate-btn">
                <Calculator size={18} /> {calculating ? 'A calcular...' : 'Calcular equivalências'}
              </button>
            )}

            {/* Calculation result preview */}
            {calcResult && !calcResult.error && (
              <div className="bg-brand-mint/10 border border-brand-mint/20 rounded-xl p-4">
                <p className="text-xs text-brand-green font-semibold uppercase tracking-wider mb-2">Resultado</p>
                <p className="text-sm text-brand-text mb-2">
                  <b>{calcResult.reference?.quantity}g</b> {calcResult.reference?.food_name} = <b>{calcResult.reference?.nutrient_value}</b> {nutrient === 'energia_kcal' ? 'kcal' : 'g'} de {nutrientLabel(nutrient).toLowerCase()}
                </p>
                {calcResult.results?.map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] text-brand-green font-semibold">ou</span>
                    {r.equivalent_portion ? (
                      <span className="text-sm text-brand-text">
                        {r.measure_display ? (
                          <><b>{r.measure_display}</b> de {r.food_name} ({r.equivalent_portion}g)</>
                        ) : (
                          <><b>{r.equivalent_portion}g</b> {r.food_name}</>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-red-500">{r.food_name}: {r.error}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {calcResult?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-xs text-red-600 flex items-center gap-1"><WarningCircle size={14} /> {calcResult.message}</p>
              </div>
            )}

            {/* Save */}
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-group-btn">Guardar grupo</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
